import { type Statement, type Team } from 'launchdarkly-api-typescript';

import { observableToPromise } from '../../../../shared/observableToPromise';
import { getTeamWithRolesAndAttributes } from '../../../api';
import { type Account, type CustomRole, type Member } from '../../../model';

import {
  type ConsolidationInfo,
  extractRoleAttributePlaceholders,
  findMissingRoleAttributes,
  getEffectiveCustomRolePermissions,
  groupSimilarPolicies,
} from './consolidation';
import { createResolvedRole, hasRoleAttributePlaceholders } from './roleResolution';

export type ConsolidationData = {
  consolidatedRole: { policy: Statement[] };
  resolvedConsolidatedRole: { policy: unknown };
  consolidationInfo: ConsolidationInfo;
  hasPlaceholders: boolean;
  mergedRoleAttributes: { [attributeName: string]: string[] };
};

export type ConsolidationDependencies = {
  customRoles: { [roleId: string]: CustomRole };
  customRolesWithTeamsForMember: (member: Member, customRoles: { [roleId: string]: CustomRole }) => unknown[];
  teamData: { [teamKey: string]: { roleAttributes: { [attributeName: string]: string[] } } };
  updateTeamData: (teamKey: string, roleAttributes: { [attributeName: string]: string[] }) => void;
};

function extractCustomRoleBaseRoles(customRoles: CustomRole[]): {
  hasBaseRoles: boolean;
  uniqueBaseRoles: string[];
  isConsistent: boolean;
  dominantBaseRole?: string;
  rolesByBaseRole: { [baseRole: string]: string[] };
} {
  if (customRoles.length === 0) {
    return {
      hasBaseRoles: false,
      uniqueBaseRoles: [],
      isConsistent: true,
      rolesByBaseRole: {},
    };
  }

  const rolesByBaseRole: { [baseRole: string]: string[] } = {};

  customRoles.forEach((role: CustomRole & { baseRole?: string; basePermissions?: string }) => {
    const baseRole = role.baseRole || role.basePermissions || 'no_access';
    if (!rolesByBaseRole[baseRole]) {
      rolesByBaseRole[baseRole] = [];
    }
    rolesByBaseRole[baseRole].push(role.name);
  });

  const uniqueBaseRoles = Object.keys(rolesByBaseRole);
  const isConsistent = uniqueBaseRoles.length === 1;

  let dominantBaseRole: string | undefined;
  if (uniqueBaseRoles.includes('writer')) {
    dominantBaseRole = 'writer';
  } else if (uniqueBaseRoles.includes('reader')) {
    dominantBaseRole = 'reader';
  } else if (uniqueBaseRoles.includes('no_access')) {
    dominantBaseRole = 'no_access';
  } else {
    [dominantBaseRole] = uniqueBaseRoles;
  }

  return {
    hasBaseRoles: uniqueBaseRoles.length > 0,
    uniqueBaseRoles,
    isConsistent,
    dominantBaseRole,
    rolesByBaseRole,
  };
}

async function fetchMissingTeamData(
  teamsNeedingData: Array<{ teamKey: string; teamName: string }>,
  account: Account,
  updateTeamData: (teamKey: string, roleAttributes: { [attributeName: string]: string[] }) => void,
): Promise<void> {
  if (teamsNeedingData.length === 0) {
    return;
  }

  const fetchPromises = teamsNeedingData.map(({ teamKey }) =>
    observableToPromise(getTeamWithRolesAndAttributes(teamKey, account))
      .then((response: Team) => {
        console.log('Team data fetched successfully:', response);
        updateTeamData(teamKey, response.roleAttributes || {});
        return response.roleAttributes || {};
      })
      .catch((e: unknown) => {
        console.error(`Failed to fetch team data for ${teamKey}:`, e);
        return {};
      }),
  );

  await Promise.all(fetchPromises);
}

function mergeRoleAttributes(
  teamRolesData: Array<{ role: CustomRole; teamKey: string; teamName: string }>,
  teamData: { [teamKey: string]: { roleAttributes?: { [attributeName: string]: string[] } } },
  member: Member,
): { [attributeName: string]: string[] } {
  const mergedAttributes: { [attributeName: string]: string[] } = {};

  if (member.roleAttributes) {
    Object.entries(member.roleAttributes).forEach(([attrName, values]) => {
      mergedAttributes[attrName] = [...values];
    });
  }

  teamRolesData.forEach(({ teamKey }) => {
    const teamRoleAttributes = teamData[teamKey]?.roleAttributes;
    if (teamRoleAttributes) {
      Object.entries(teamRoleAttributes).forEach(([attrName, values]) => {
        if (mergedAttributes[attrName]) {
          // Merge values, removing duplicates
          const allValues = [...mergedAttributes[attrName], ...values];
          mergedAttributes[attrName] = Array.from(new Set(allValues));
        } else {
          mergedAttributes[attrName] = [...values];
        }
      });
    }
  });

  return mergedAttributes;
}

export async function consolidatePermissions(
  member: Member,
  account: Account,
  dependencies: ConsolidationDependencies,
): Promise<ConsolidationData> {
  const { customRoles, customRolesWithTeamsForMember, teamData, updateTeamData } = dependencies;

  const directCustomRoles = (member.customRoles || [])
    .map((roleId: string) => customRoles[roleId])
    .filter((role): role is CustomRole => Boolean(role));

  const teamRolesData = Array.isArray(customRolesWithTeamsForMember(member, customRoles))
    ? (
        customRolesWithTeamsForMember(member, customRoles) as Array<{
          role: { _id: string };
          team: { key: string; name: string };
        }>
      )
        .map(({ role, team }) => ({
          role: customRoles[role._id],
          teamKey: team.key,
          teamName: team.name,
        }))
        .filter((item): item is { role: CustomRole; teamKey: string; teamName: string } =>
          Boolean(item.role && item.teamKey && item.teamName),
        )
    : [];

  const teamsNeedingDataMap = new Map<string, { teamKey: string; teamName: string }>();
  teamRolesData.forEach((item) => {
    const { teamKey, teamName } = item;
    if (!teamData[teamKey]?.roleAttributes && !teamsNeedingDataMap.has(teamKey)) {
      teamsNeedingDataMap.set(teamKey, { teamKey, teamName });
    }
  });
  const teamsNeedingData = Array.from(teamsNeedingDataMap.values());

  await fetchMissingTeamData(teamsNeedingData, account, updateTeamData);

  const allCustomRoles = [...directCustomRoles, ...teamRolesData.map(({ role }) => role)];
  const customRoleBaseRoles = extractCustomRoleBaseRoles(allCustomRoles);

  const allPolicies: Statement[] = [];

  directCustomRoles.forEach((role: CustomRole) => {
    const effectivePermissions = getEffectiveCustomRolePermissions(role);
    allPolicies.push(...effectivePermissions);
  });

  teamRolesData.forEach(({ role }) => {
    const effectivePermissions = getEffectiveCustomRolePermissions(role);
    allPolicies.push(...effectivePermissions);
  });

  const groupedPolicies = groupSimilarPolicies(allPolicies);

  const allMergedRoleAttributes = mergeRoleAttributes(teamRolesData, teamData, member);

  const referencedAttributeNames = extractRoleAttributePlaceholders(groupedPolicies);
  const mergedRoleAttributes: { [attributeName: string]: string[] } = {};

  referencedAttributeNames.forEach((attrName) => {
    // Include all referenced attributes, even if they have no values (empty array)
    mergedRoleAttributes[attrName] = allMergedRoleAttributes[attrName] || [];
  });

  const consolidatedRole = {
    policy: groupedPolicies,
  };

  const consolidatedRoleWithAttributes: CustomRole = {
    _id: `consolidated-${member._id}`,
    _links: {}, // Required by type but not displayed
    key: `consolidated-${member._id}`,
    name: `Consolidated Permissions: ${member.email}`,
    description: `All effective permissions for ${member.email}`,
    policy: groupedPolicies,
    roleAttributes: mergedRoleAttributes,
  } as CustomRole;

  const fullResolvedRole = createResolvedRole(consolidatedRoleWithAttributes, member, mergedRoleAttributes);

  // Create a clean resolved role for display (only show the policy)
  const resolvedConsolidatedRole = {
    policy: fullResolvedRole.policy,
  } as { policy: unknown };

  const hasPlaceholders = hasRoleAttributePlaceholders(consolidatedRoleWithAttributes);

  const missingAttributes = findMissingRoleAttributes(groupedPolicies, mergedRoleAttributes);

  const consolidationInfo: ConsolidationInfo = {
    hasBaseRole: teamRolesData.length > 0 && directCustomRoles.length === 0,
    baseRole: member.role,
    hasDirectCustomRoles: directCustomRoles.length > 0,
    directCustomRoles,
    hasTeamRoles: teamRolesData.length > 0,
    teamRoles: teamRolesData,
    conflicts: [],
    roleAttributeSources: [],
    missingRoleAttributes: missingAttributes,
    customRoleBaseRoles,
  };

  return {
    consolidatedRole,
    resolvedConsolidatedRole,
    consolidationInfo,
    hasPlaceholders,
    mergedRoleAttributes,
  };
}

export function createConsolidationService(dependencies: ConsolidationDependencies) {
  return {
    consolidatePermissions: (member: Member, account: Account) => consolidatePermissions(member, account, dependencies),
  };
}
